<script setup>
import { XerButton } from '@xerius/codey-components-vue2';
import StartTypeEnum from '@/stores/lead-aanvraag/StartType.enum';
import { useRootStore } from '@/stores';
import { useLeadAanvraagStore } from '@/stores/lead-aanvraag/lead-aanvraag';
import { computed, defineProps, ref, defineEmits, onMounted } from 'vue';
import { useFlowNavigation } from '@/use/use-flow-navigation';
import { useAanbodExtensions } from '@/use/aanbod-definitie/use-aanbod-extensions';
import { useRouteExtensions } from '@/use/use-route-extensions';

const emit = defineEmits(['on-unknown-auto-start-detected', 'on-anonymous-login-clicked']);
const props = defineProps({
  anonymousLabel: { type: String, default: 'Moez.Verderknop.zonderitsme' },
  startAanbodBeforeLogin: { type: Boolean, default: false },
  autoStart: { type: String, default: undefined }
});

const rootStore = useRootStore();
const leadAanvraagStore = useLeadAanvraagStore();
const computedAanvraagId = computed(() => leadAanvraagStore.aanvraagId);

//When no aanvraagId is found, get aanbod by naam
const { aanbodDefinitie } = useAanbodExtensions(!!computedAanvraagId);
const { subflowNaam } = useRouteExtensions();
const formTargetItsMeUrl = computed(() => {
  return `${process.env.VUE_APP_API_ENDPOINT}/login?lang=${rootStore.lang}&aanvraagId=${computedAanvraagId.value}&subflow=${subflowNaam.value}&start=${StartTypeEnum.Itsme}`;
});
const formTargetXeriusLoginUrl = computed(() => {
  return `${process.env.VUE_APP_API_ENDPOINT}/XeriusAccountLogin?lang=${rootStore.lang}&aanvraagId=${computedAanvraagId.value}&subflow=${subflowNaam.value}&start=${StartTypeEnum.XeriusAccount}`;
});
const showItsmeLogin = computed(() => {
  return (
    rootStore.config.allowLoginWithItsme === 'true' &&
    aanbodDefinitie?.value?.enabledLoginTypes &&
    aanbodDefinitie?.value?.enabledLoginTypes.includes('Itsme')
  );
});
const showXeriusAccountLogin = computed(() => {
  return (
    rootStore.config.allowLoginWithXeriusAccount === 'true' &&
    aanbodDefinitie?.value?.enabledLoginTypes &&
    aanbodDefinitie?.value?.enabledLoginTypes.includes('XeriusAccount')
  );
});
const showAnonymous = computed(() => {
  return rootStore.config.allowAnonymous === 'true' && aanbodDefinitie?.value?.allowAnonymous;
});
const areLoginMethodsAvailable = computed(() => {
  return showItsmeLogin.value || showXeriusAccountLogin.value || showAnonymous.value;
});

//$refs
const itsmeForm = ref(undefined);
const xeriusLoginForm = ref(undefined);

const flowNavigation = useFlowNavigation();
const { loading } = useFlowNavigation();

async function submitItsme() {
  if (!showItsmeLogin) {
    emit('on-unknown-auto-start-detected');
    return;
  }

  if (props.startAanbodBeforeLogin) {
    await flowNavigation.startAanbod();
  }
  setTimeout(() => {
    itsmeForm.value.submit();
  }, 2000);
}
async function submitXeriusLogin() {
  if (!showXeriusAccountLogin) {
    emit('on-unknown-auto-start-detected');
    return;
  }

  if (props.startAanbodBeforeLogin) {
    await flowNavigation.startAanbod();
  }

  xeriusLoginForm.value.submit();
}

function submitAnonymous() {
  if (!showAnonymous.value) {
    emit('on-unknown-auto-start-detected');
    return;
  }
  emit('on-anonymous-login-clicked');
}

onMounted(() => {
  switch (props.autoStart?.toLowerCase()) {
    case 'itsme':
      submitItsme();
      break;
    case 'xeriusaccount':
      submitXeriusLogin();
      break;
    case 'anonymous':
      submitAnonymous();
      break;
    default:
      emit('on-unknown-auto-start-detected');
      break;
  }
});
</script>
<template>
  <div v-show="!autoStart" data-automation-id="xer-login">
    <form
      v-if="showItsmeLogin"
      ref="itsmeForm"
      class="xer-flex xer-justify-end xer-mb-5"
      method="POST"
      :action="formTargetItsMeUrl"
    >
      <xer-button
        data-automation-id="login-itsme"
        xer-icon="itsme-color"
        @click.prevent="submitItsme"
      >
        <span v-html="rootStore.translate('Moez.Verderknop.metitsme')" />
      </xer-button>
    </form>

    <form
      v-if="showXeriusAccountLogin"
      ref="xeriusLoginForm"
      class="xer-flex xer-justify-end xer-mb-5"
      method="POST"
      :action="formTargetXeriusLoginUrl"
    >
      <xer-button
        data-automation-id="login-xerius-account"
        xer-icon="xerius-logo-color"
        @click.prevent="submitXeriusLogin"
      >
        <span v-html="rootStore.translate('Moez.Verderknop.xeriusAccount')" />
      </xer-button>
    </form>

    <div v-if="showAnonymous" class="xer-flex xer-justify-end">
      <xer-button
        type="button"
        xer-icon="chevron-right"
        data-automation-id="navigatie-verder"
        :xer-loading="loading"
        @click="submitAnonymous()"
      >
        <span v-html="rootStore.translate(anonymousLabel)" />
      </xer-button>
    </div>
    <div
      v-if="!areLoginMethodsAvailable"
      class="xer-alert xer-alert--error"
      data-automation-id="no-login-available"
    >
      <p class="xer-alert__message" v-html="rootStore.translate('Login.Fallback')"></p>
    </div>
  </div>
</template>
